import React from 'react';
import PropTypes from 'prop-types';

// Lets an item close its menu after acting (opt-in via `closeOnClick`).
const DropdownContext = React.createContext({ close: () => {} });

const Dropdown = ({
	trigger,
	children,
	className = '',
	width = 'w-56',
	align = 'right',
	onOpen,
	onClose,
	...props
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const dropdownRef = React.useRef(null);

	const toggleDropdown = () => {
		const newState = !isOpen;
		setIsOpen(newState);

		// Call callbacks when state changes
		if (newState && onOpen) {
			onOpen();
		} else if (!newState && onClose) {
			onClose();
		}
	};

	const closeDropdown = () => {
		if (isOpen) {
			setIsOpen(false);
			if (onClose) {
				onClose();
			}
		}
	};

	// Close dropdown when clicking outside
	React.useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				closeDropdown();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	const alignmentClass = align === 'left' ? 'left-0' : 'right-0';

	return (
		<div
			className={`relative inline-block text-left ${className}`}
			ref={dropdownRef}
			{...props}>
			<div onClick={toggleDropdown} className='cursor-pointer'>
				{trigger}
			</div>
			{isOpen && (
				<div
					className={`absolute ${alignmentClass} z-10 mt-2 ${width} origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}>
					<div
						className='py-1'
						role='menu'
						aria-orientation='vertical'
						aria-labelledby='options-menu'>
						<DropdownContext.Provider value={{ close: closeDropdown }}>
							{children}
						</DropdownContext.Provider>
					</div>
				</div>
			)}
		</div>
	);
};

const DropdownItem = ({
	className = '',
	onClick,
	children,
	disabled = false,
	closeOnClick = false,
	...props
}) => {
	const { close } = React.useContext(DropdownContext);
	const handleClick = (e) => {
		if (disabled) return;
		if (closeOnClick) close();
		if (onClick) {
			onClick(e);
		}
	};

	const stateClass = disabled
		? 'text-gray-400 cursor-not-allowed'
		: 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer';

	return (
		<div
			className={`block px-4 py-2 text-sm ${stateClass} ${className}`}
			role='menuitem'
			aria-disabled={disabled || undefined}
			onClick={handleClick}
			{...props}>
			{children}
		</div>
	);
};

const DropdownSeparator = ({ className = '', ...props }) => {
	return <div className={`my-1 h-px bg-gray-200 ${className}`} {...props} />;
};

Dropdown.propTypes = {
	trigger: PropTypes.node.isRequired,
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	width: PropTypes.string,
	align: PropTypes.string,
	onOpen: PropTypes.func,
	onClose: PropTypes.func,
};

DropdownItem.propTypes = {
	className: PropTypes.string,
	onClick: PropTypes.func,
	children: PropTypes.node,
	disabled: PropTypes.bool,
	closeOnClick: PropTypes.bool,
};

DropdownSeparator.propTypes = {
	className: PropTypes.string,
};

Dropdown.Item = DropdownItem;
Dropdown.Separator = DropdownSeparator;

export default Dropdown;
